import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import './BlogPreviewSection.css';

const BLOG_URL = import.meta.env.VITE_BLOG_URL || 'https://blog.raisa.chat';
const BACKEND_URL = import.meta.env.VITE_API_URL || '';

// Default image for itineraries without hero image
const DEFAULT_ITINERARY_IMAGE = '/images/default-itinerary.jpg';

interface CityGuide {
    id: string;
    slug: string;
    name: string;
    type: string;
    heroImageUrl?: string;
    itineraryCount: number;
    metaDescription?: string;
}

interface Itinerary {
    id: string;
    canonicalSlug: string;
    metaTitle: string;
    metaDescription?: string;
    tags: string[];
    heroImageUrl?: string;
}

interface BlogPreviewSectionProps {
    locale: string;
    id?: string;
}

// SVG Icons
const MapPinIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
    </svg>
);

const CompassIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </svg>
);

const GlobeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
        <path d="M2 12h20"/>
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);

export function BlogPreviewSection({ locale, id }: BlogPreviewSectionProps) {
    const { t } = useTranslation();
    const [cityGuides, setCityGuides] = useState<CityGuide[]>([]);
    const [itineraries, setItineraries] = useState<Itinerary[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            try {
                const [guidesRes, itinerariesRes] = await Promise.all([
                    fetch(`${BACKEND_URL}/api/city-guides/popular?locale=${locale}&limit=3`),
                    fetch(`${BACKEND_URL}/api/seo/itineraries/featured?locale=${locale}&limit=6`),
                ]);

                if (guidesRes.ok) {
                    const guidesData = await guidesRes.json();
                    setCityGuides(guidesData.guides || []);
                }

                if (itinerariesRes.ok) {
                    const itinerariesData = await itinerariesRes.json();
                    setItineraries(itinerariesData.itineraries || []);
                }
            } catch (error) {
                logger.error('Failed to fetch blog preview data:', error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [locale]);

    // Don't render if no content
    if (!loading && cityGuides.length === 0 && itineraries.length === 0) {
        return null;
    }

    // Get image URL with fallback
    const getImageUrl = (heroImageUrl: string | undefined | null, isItinerary = false) => {
        if (heroImageUrl) {
            return `${BACKEND_URL}/${heroImageUrl}`;
        }
        return isItinerary ? DEFAULT_ITINERARY_IMAGE : null;
    };

    // Skeleton loader
    if (loading) {
        return (
            <section className="blog-preview-section" id={id}>
                <div className="blog-preview-container">
                    <div className="blog-preview-skeleton">
                        <div className="skeleton-header" />
                        <div className="skeleton-grid-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="skeleton-card" />
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="blog-preview-section" id={id}>
            <div className="blog-preview-container">
                {/* Section Header */}
                <header className="blog-section-header">
                    <span className="blog-section-badge">
                        <GlobeIcon />
                        {t('home.explore.badge')}
                    </span>
                    <h2 className="blog-section-title">{t('home.explore.title')}</h2>
                    <p className="blog-section-subtitle">{t('home.explore.subtitle')}</p>
                    <p className="blog-section-hint">{t('home.explore.subtitleHint')}</p>
                </header>

                {/* City Guides */}
                {cityGuides.length > 0 && (
                    <div className="blog-block">
                        <div className="blog-block-header">
                            <h3 className="blog-block-title">
                                <span className="title-icon"><MapPinIcon /></span>
                                {t('home.explore.cityGuides')}
                            </h3>
                            <a 
                                href={`${BLOG_URL}/${locale}/city-guides`} 
                                className="blog-view-all"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('home.explore.viewGuides')}
                                <ArrowRightIcon />
                            </a>
                        </div>
                        
                        <div className="city-guides-grid-equal">
                            {cityGuides.map((guide) => {
                                const imageUrl = getImageUrl(guide.heroImageUrl);
                                return (
                                    <a
                                        key={guide.id}
                                        href={`${BLOG_URL}/${locale}/city-guides/${guide.slug}`}
                                        className="city-guide-card"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <div className="city-guide-image">
                                            {imageUrl ? (
                                                <img 
                                                    src={imageUrl} 
                                                    alt={guide.name}
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="city-guide-placeholder">
                                                    <MapPinIcon />
                                                </div>
                                            )}
                                            <div className="city-guide-overlay" />
                                        </div>
                                        <div className="city-guide-content">
                                            {guide.itineraryCount > 0 && (
                                                <span className="city-guide-badge">
                                                    {guide.itineraryCount} {t('home.explore.itineraryCount')}
                                                </span>
                                            )}
                                            <h4 className="city-guide-name">{guide.name}</h4>
                                            <span className="city-guide-cta">
                                                {t('home.explore.readMore')}
                                                <ArrowRightIcon />
                                            </span>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Featured Itineraries */}
                {itineraries.length > 0 && (
                    <div className="blog-block">
                        <div className="blog-block-header">
                            <h3 className="blog-block-title">
                                <span className="title-icon"><CompassIcon /></span>
                                {t('home.explore.itineraries')}
                            </h3>
                            <a 
                                href={`${BLOG_URL}/${locale}`} 
                                className="blog-view-all"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('home.explore.viewItineraries')}
                                <ArrowRightIcon />
                            </a>
                        </div>
                        
                        <div className="itineraries-grid">
                            {itineraries.map((itinerary) => {
                                const imageUrl = getImageUrl(itinerary.heroImageUrl, true);
                                return (
                                    <a
                                        key={itinerary.id}
                                        href={`${BLOG_URL}/${locale}/itinerary/${itinerary.canonicalSlug}`}
                                        className="itinerary-card"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <div className="itinerary-image">
                                            <img 
                                                src={imageUrl!} 
                                                alt={itinerary.metaTitle}
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="itinerary-content">
                                            <h4 className="itinerary-title">{itinerary.metaTitle}</h4>
                                            {itinerary.tags && itinerary.tags.length > 0 && (
                                                <div className="itinerary-tags">
                                                    {itinerary.tags.slice(0, 2).map((tag) => (
                                                        <span key={tag} className="itinerary-tag">{tag}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
